// types
import { FeesCategoriesType } from '@makemydeal/dr-shared-types';
import type * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import type { FeeOverride, IDealerFee, ItemizedGovernmentFee, paymentServicesTypes } from '@makemydeal/dr-platform-types';
import type { FeeCategory } from '@makemydeal/dr-shared-types';
import type { CustomFeeItem, DealerFeeWithIncludeCal, Detail, GovernmentFeeWithIncludeCal, LeaseDetail } from './types';

const ELIMINATED_FEE_AMOUNT = 0;

export const isLastRow = <T>({ index, rows }: { index: number; rows: T[] }) => {
    return index === rows.length - 1;
};

export const isLeaseFinanceDetail = (isLease: boolean, feeDetailRow: Detail): feeDetailRow is LeaseDetail => isLease;

export const getFeeDetailsRows = ({ feeItems, isLease }: { isLease: boolean; feeItems: CustomFeeItem[] }) => {
    return feeItems.filter(Boolean).map((feeItem) => {
        const feeDetailRow: Detail = {
            name: feeItem.feeName,
            code: feeItem.feeTypeId,
            amount: `${feeItem.feeAmount}`,
            category: feeItem.category,
            isManual: feeItem.isManualFee,
            isEdited: feeItem.isEdited,
            isDeleted: feeItem.isDeleted,
            includedInCalc: !!feeItem.includedInCalc,
            isTaxed: feeItem.isTaxed
        };

        if (isLeaseFinanceDetail(isLease, feeDetailRow)) {
            const isFeeUpfront = !feeItem.capIndicator;
            feeDetailRow.upfront = isFeeUpfront;
        }

        return feeDetailRow;
    });
};

export const hasChangeGovFees = ({
    initialGovFees,
    updatedGovFees
}: {
    initialGovFees: CustomFeeItem[];
    updatedGovFees: CustomFeeItem[];
}) => {
    return JSON.stringify(initialGovFees) !== JSON.stringify(updatedGovFees);
};

export const hasChangeDealerFees = ({
    initialDealerFees,
    updatedDealerFees
}: {
    initialDealerFees: CustomFeeItem[];
    updatedDealerFees: CustomFeeItem[];
}) => {
    return JSON.stringify(initialDealerFees) !== JSON.stringify(updatedDealerFees);
};

export const hasChangeLenderFees = ({
    initialLenderFees,
    updatedLenderFees
}: {
    initialLenderFees: CustomFeeItem[];
    updatedLenderFees: CustomFeeItem[];
}) => {
    return JSON.stringify(initialLenderFees) !== JSON.stringify(updatedLenderFees);
};

export const getAreFeesValid = (
    dealerFees: CustomFeeItem[],
    govFees: CustomFeeItem[],
    lenderFees: CustomFeeItem[],
    requireFeeName = false
): boolean => {
    const validator = (fee: CustomFeeItem): boolean => {
        return Boolean(fee.feeTypeId && (requireFeeName ? Boolean(fee.feeName) : true) && parseInt(fee.feeAmount) >= 0);
    };

    const areLenderFeesValid = lenderFees.every(validator);
    const areDealerFeesValid = dealerFees.every(validator);
    const areGovernmentFeesValid = govFees.every(validator);

    return areDealerFeesValid && areGovernmentFeesValid && areLenderFeesValid;
};

const isGovernmentFees = (
    fee: DealerFeeWithIncludeCal | GovernmentFeeWithIncludeCal,
    category: paymentServicesTypes.FeeCategory
): fee is GovernmentFeeWithIncludeCal => {
    return category === FeesCategoriesType.GOVERNMENT;
};

// Helper to get includedInCalc with legacy fallbacks
const getIncludedInCalc = (fee: DealerFeeWithIncludeCal | GovernmentFeeWithIncludeCal): boolean => {
    if (fee.isFeeIncludedInCalculation !== undefined) {
        return fee.isFeeIncludedInCalculation;
    }
    /* istanbul ignore next */
    if (fee.includedInCalc !== undefined) {
        return fee.includedInCalc;
    }
    /* istanbul ignore next */
    return true;
};

export const getCustomFeeItemsAdapter = (
    feeItem: (DealerFeeWithIncludeCal | GovernmentFeeWithIncludeCal)[],
    categoryType: paymentServicesTypes.FeeCategory,
    deletedFees: CustomFeeItem[] = []
): CustomFeeItem[] => {
    return feeItem.map((fee) => {
        const isGovFee = isGovernmentFees(fee, categoryType);

        const deletedFee = deletedFees.find(({ category, feeTypeId }) => {
            return (
                category === categoryType &&
                ((isGovFee && feeTypeId === fee.code && fee.amount === 0) ||
                    (!isGovFee && feeTypeId === fee.dealerFeeTypeCode && fee.dealerFeeAmount === 0))
            );
        });

        if (deletedFee) {
            return deletedFee;
        }

        if (isGovFee) {
            return {
                feeTypeId: fee.code,
                feeAmount: `${fee.amount}`,
                capIndicator: fee.capped,
                feeDescription: fee.description,
                feeName: fee.name || fee.description,
                category: categoryType,
                isManualFee: fee.isManualFee,
                includedInCalc: getIncludedInCalc(fee),
                isDeleted: /* istanbul ignore next */ fee.isManualFee && fee.amount === 0,
                isEdited: /* istanbul ignore next */ fee.isManualFee && fee.amount > 0,
                isTaxed: fee.isTaxed
            };
        }

        // Legacy fallbacks not covered in tests
        return {
            feeTypeId: fee.dealerFeeTypeCode,
            feeAmount: `${fee.dealerFeeAmount}`,
            capIndicator: fee.dealerFeeCapped,
            feeDescription: fee.dealerFeeDesc,
            feeName: fee.dealerFeeName || fee.dealerFeeDesc,
            category: categoryType,
            includedInCalc: getIncludedInCalc(fee),
            isManualFee: fee.isManualFee,
            isDeleted: /* istanbul ignore next */ fee.isManualFee && fee.dealerFeeAmount === 0,
            isEdited: /* istanbul ignore next */ fee.isManualFee && fee.dealerFeeAmount > 0,
            isTaxed: fee.isTaxed
        };
    });
};

type IdentifiableFee = {
    dealerFeeTypeCode?: number;
    code?: number;
    id?: number;
    feeTypeId?: number;
    feeAmount?: string;
    dealerFeeAmount?: number;
    amount?: number;
};

export const getFeeCodes = (feeItems: IdentifiableFee[]): number[] => {
    if (!feeItems?.length) return [];

    return feeItems
        .filter((fee) => {
            return fee.dealerFeeAmount !== undefined ? fee.dealerFeeAmount !== 0 : fee.amount !== 0;
        })
        .map((fee) => fee.dealerFeeTypeCode || fee.code || fee.id || fee.feeTypeId || -1);
};
//TODO combine getFeeCodes and getFeeCodesForItemizedFees into one function
export const getFeeCodesForItemizedFees = (feeItems: IdentifiableFee[]): number[] => {
    if (!feeItems.length) return [];

    return feeItems
        .filter((fee) => {
            return Number(fee.feeAmount) !== 0;
        })
        .map((fee) => fee.dealerFeeTypeCode || fee.code || fee.id || fee.feeTypeId || -1);
};

export const filterFeesById = (fees: FeeCategory[], exclusionList: number[]) => {
    return fees.filter((fee) => !exclusionList.includes(fee.id));
};

export const mergeFeeUpdates = (fees: CustomFeeItem[]): CustomFeeItem[] => {
    return fees.reduce((acc, current) => {
        const existingIndex = acc.findIndex((item) => item.feeTypeId === current.feeTypeId && item.isDeleted);
        if (existingIndex !== -1) {
            acc[existingIndex] = current; // Update existing deleted fee
        } else {
            acc.push(current); // Add new fee
        }
        return acc;
    }, [] as CustomFeeItem[]);
};

export const getIsAllFeesCappedByCategory = (
    fees: IDealerFee[] | offerSelectors.DealerFeeWithOriginalCategory[] | ItemizedGovernmentFee[],
    category: paymentServicesTypes.FeeCategory
): boolean => {
    return fees.every((fee) => {
        if (category === FeesCategoriesType.GOVERNMENT) {
            return !!(fee as ItemizedGovernmentFee).capped;
        }

        return !!(fee as IDealerFee).dealerFeeCapped;
    });
};

export const DEFAULT_FEE_ROW_ERROR_STATE = {
    feeTypeId: false,
    feeName: false,
    feeAmount: false,
    isDeleted: false
};

export const getDefaultFeeRowErrorState = () => ({ ...DEFAULT_FEE_ROW_ERROR_STATE });

/**
 * Applies fee overrides (name and amount) from feesOverride onto a list of CustomFeeItems.
 * Used to keep displayed values in sync with Redux when no payment call has completed yet.
 */
export const applyFeeOverrides = (items: CustomFeeItem[], overrides: FeeOverride[] | undefined): CustomFeeItem[] => {
    if (!overrides?.length) return items;
    return items.map((item) => {
        const override = overrides.find((o) => o.feeTypeId === item.feeTypeId && o.category === item.category);
        if (override === undefined) return item;
        const isDeleted = override.feeAmount === ELIMINATED_FEE_AMOUNT;
        return {
            ...item,
            isDeleted,
            ...(override.feeName !== undefined && { feeName: override.feeName }),
            ...(!isDeleted && override.feeAmount !== undefined && { feeAmount: String(override.feeAmount) })
        };
    });
};
